import * as Notifications from 'expo-notifications';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import type { DrivingLimitStatus } from '../api/apiService';
import { formatDrivingDuration, formatLimitHours } from './drivingLimit';
import { DRIVING_STATUS_NOTIFICATION_CATEGORY, DRIVING_STATUS_NOTIFICATION_ID } from './drivingNotifications';

// Countdown to the rolling 24h driving limit (Lei 45/2018, art. 13) in the
// persistent "Driving" notification. The background location task (a
// foreground service, ~30 s ticks whether or not the app is in front) refreshes
// it from the last status the app received plus the time elapsed since, so no
// API call is made per tick. Without background tracking nothing refreshes it
// and the notification keeps its usual text.
export const DRIVING_LIMIT_SNAPSHOT_KEY = 'drivingLimitSnapshot';

// A snapshot older than the window says nothing about the current one.
const SNAPSHOT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface DrivingLimitSnapshot {
  limit: DrivingLimitStatus;
  receivedAt: number;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const storeDrivingLimitSnapshot = async (limit: DrivingLimitStatus | null | undefined, receivedAt = Date.now()) => {
  try {
    if (limit) {
      await AsyncStorage.setItem(DRIVING_LIMIT_SNAPSHOT_KEY, JSON.stringify({ limit, receivedAt }));
    } else {
      await AsyncStorage.removeItem(DRIVING_LIMIT_SNAPSHOT_KEY);
    }
  } catch (error) {
    console.warn('Failed to store driving limit snapshot', error);
  }
};

/** Countdown line for the snapshot at `nowMs`, or null when it does not apply. */
export const drivingLimitCountdownText = (
  snapshot: DrivingLimitSnapshot | null,
  nowMs: number,
  t: Translate,
  language?: string,
): string | null => {
  if (!snapshot?.limit?.is_driving) return null;
  const elapsedMs = nowMs - snapshot.receivedAt;
  if (!(elapsedMs >= 0) || elapsedMs > SNAPSHOT_MAX_AGE_MS) return null;
  const { limit } = snapshot;
  // Only in the warning window or once reached. A status received as 'ok'
  // enters the window by itself once seconds_until_warning has elapsed.
  if (limit.status === 'ok'
    && (limit.seconds_until_warning == null || limit.seconds_until_warning - elapsedMs / 1000 > 0)) return null;
  const hours = formatLimitHours(limit.limit_seconds, language);
  // While driving the server gives seconds_until_limit unless already reached;
  // it already accounts for older driving leaving the window.
  const remaining = limit.status === 'reached' || limit.seconds_until_limit == null
    ? 0
    : limit.seconds_until_limit - elapsedMs / 1000;
  if (remaining <= 0) return t('drivingLimit.noticeReached', { hours });
  return t('drivingLimit.noticeWarning', { remaining: formatDrivingDuration(remaining), hours });
};

/**
 * Re-post the persistent "Driving" notification with the countdown, in place
 * (same identifier, no dismiss). `sound: false` and no vibration make
 * expo-notifications post it silent, so a refresh never rings, vibrates or
 * shows a heads-up. Android only: iOS would present the banner again.
 */
export const refreshDrivingStatusCountdown = async (t: Translate, language?: string, nowMs = Date.now()) => {
  if (Platform.OS !== 'android') return;
  try {
    // Read right before posting: driving may have stopped since the tick began.
    const [[, isDriving], [, raw]] = await AsyncStorage.multiGet(['isDriving', DRIVING_LIMIT_SNAPSHOT_KEY]);
    if (isDriving !== 'true' || !raw) return;
    const snapshot = JSON.parse(raw) as DrivingLimitSnapshot;
    const countdown = drivingLimitCountdownText(snapshot, nowMs, t, language);
    if (!countdown) return;
    await Notifications.scheduleNotificationAsync({
      content: {
        title: t('notifications.driving'),
        body: countdown,
        sticky: true,
        categoryIdentifier: DRIVING_STATUS_NOTIFICATION_CATEGORY,
        data: { isDriving: true },
        sound: false,
      },
      trigger: null,
      identifier: DRIVING_STATUS_NOTIFICATION_ID,
    });
  } catch (error) {
    console.warn('Failed to refresh the driving limit countdown', error);
  }
};
